import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCreditosControllerSaldo } from '@/api/generated/créditos-de-discussão/créditos-de-discussão';
import { useDiscussoesControllerEnviarMensagem } from '@/api/generated/discussões-técnicas/discussões-técnicas';
import { usePagamentosControllerAbrirComCredito } from '@/api/generated/discussões-técnicas-pagamento/discussões-técnicas-pagamento';
import type { MensagemPendente } from '@/features/discussoes/tipos';
import { linkDeVoltaParaOApp, voltarParaOApp } from '@/features/discussoes/voltarParaOApp';
import { rotaDaDiscussao } from '@/shared/lib/rotas';

export type FaseDeAbertura = 'preenchendo' | 'liquidando' | 'pagamento' | 'concluidoNoApp';

/**
 * O que acontece depois que `AberturaDiscussaoWizard` termina (o rascunho já
 * existe, com tudo preenchido) — compartilhado entre `NovaDiscussaoPage`
 * (rascunho novo) e `DiscussaoDetalhePage` (retomando um rascunho existente),
 * que senão duplicariam a MESMA checagem de crédito + despacho.
 *
 * Crédito é relido AGORA, não no mount do wizard: a pessoa leva minutos nos
 * passos, e o saldo pode ter mudado nesse meio-tempo — é o que decide se a
 * etapa de pagamento aparece.
 */
export function useFluxoDeAberturaDeDiscussao(doApp: boolean) {
  const navigate = useNavigate();
  const [fase, setFase] = useState<FaseDeAbertura>('preenchendo');
  const [discussaoId, setDiscussaoId] = useState<string | null>(null);
  const [mensagensPendentes, setMensagensPendentes] = useState<MensagemPendente[]>([]);

  const { refetch: buscarSaldo } = useCreditosControllerSaldo({ query: { enabled: false } });
  const abrirComCredito = usePagamentosControllerAbrirComCredito();
  const enviarMensagem = useDiscussoesControllerEnviarMensagem();

  async function despachar(id: string, pendentes: MensagemPendente[]) {
    if (pendentes.length > 0) {
      await Promise.all(pendentes.map((m) => enviarMensagem.mutateAsync({ id, data: m }))).catch(
        () => {},
      );
    }

    if (doApp) {
      setDiscussaoId(id);
      setFase('concluidoNoApp');
      voltarParaOApp(id);
      return;
    }

    navigate(rotaDaDiscussao(id));
  }

  async function aoAbrir(id: string, pendentes: MensagemPendente[]): Promise<void> {
    setFase('liquidando');
    let abertaComCredito = false;

    try {
      const { data: credito } = await buscarSaldo();
      if ((credito?.saldo ?? 0) > 0) {
        await abrirComCredito.mutateAsync({ id });
        abertaComCredito = true;
      }
    } catch {
      // Sem crédito de verdade, advisor sem vaga ou rede: o rascunho continua
      // salvo e o caminho normal é o pagamento. Nada de erro na tela.
    }

    if (abertaComCredito) {
      // `liquidando` continua ligado de propósito: quem chamou `despachar`
      // sai desta tela (ou, na tela de detalhe, o polling do status ainda
      // não pegou a mudança) — desligar antes piscaria o formulário
      // preenchido por um frame.
      await despachar(id, pendentes);
      return;
    }

    setFase('pagamento');
    setDiscussaoId(id);
    setMensagensPendentes(pendentes);
  }

  return {
    fase,
    discussaoId,
    mensagensPendentes,
    aoAbrir,
    /** Só relevante quando `fase === 'concluidoNoApp'`. */
    linkDeVoltaParaOApp,
  };
}
